import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { notifications as source } from '../../services/notifications'
import type { NewNotificationInput, Notification } from '../../types/notifications'
import { NotificationContext, type NotificationContextValue } from './NotificationContextDef'

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Notification[]>(() => source.getAll())

  useEffect(() => {
    // Re-read on mount to close the race between the initial render snapshot
    // and subscription: a non-React caller could have invoked source.add(...)
    // between those two ticks, and subscribe() does not replay past events.
    setItems(source.getAll())
    return source.subscribe(setItems)
  }, [])

  const unreadCount = useMemo(() => items.filter((n) => n.readAt === null).length, [items])

  const add = useCallback((input: NewNotificationInput) => source.add(input), [])
  const markRead = useCallback((id: string) => source.markRead(id), [])
  const markAllRead = useCallback(() => source.markAllRead(), [])
  const remove = useCallback((id: string) => source.remove(id), [])
  const clear = useCallback(() => source.clear(), [])

  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications: items,
      unreadCount,
      add,
      markRead,
      markAllRead,
      remove,
      clear,
    }),
    [items, unreadCount, add, markRead, markAllRead, remove, clear]
  )

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
}
